import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SEO from '../SEO/SEO';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Shield, FileText, Download, CheckCircle2, Users, BarChart3, Target, Award, Eye, Lock, TrendingUp, BookOpen, UserCheck, AlertCircle, ShieldCheck, FileCheck, ExternalLink, Search } from 'lucide-react';
import './CorporateGovernance.css';
import { useNavigate } from "react-router-dom";
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

const DEFAULT_TRANSPARENCY_NOTICE = '<p>Investors and stakeholders can access Aham Housing Finance’s financial and regulatory disclosures through our <a href="https://ahamhfc.com/investor-information"><strong><u>investor information</u></strong></a> section.</p>';

const sanitizeAndFormatHtml = (content) => {
  if (!content || typeof content !== 'string') return '';
  let html = content;
  html = html.replace(/<a\s+[^>]*?>/gi, (tag) => {
    const attrMatch = tag.match(/(?:["']?(?:link|href)\s*=\s*\?["']?\s*)([^"'\>]+?)(?:\s*\?["']?\s*)(?:>|\s|$)/i);
    if (!attrMatch) return tag;
    const rawUrl = attrMatch[1].trim().replace(/^["']|["']$/g, '');
    const targetMatch = tag.match(/target\s*=\s*["']?([^"'>\s]+)["']?/i);
    const targetAttr = targetMatch ? ` target="${targetMatch[1]}"` : '';
    return `<a href="${rawUrl}"${targetAttr}>`;
  });
  if (!/<(?:br|p|div)\b/i.test(html)) {
    html = html.replace(/\r?\n/g, '<br />');
  }
  return html;
};

const CorporateGovernance = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [banner, setBanner] = useState(null);
  const [foundation, setFoundation] = useState(null);
  const [transparency, setTransparency] = useState(null);
  const [oversight, setOversight] = useState(null);
  const [ethics, setEthics] = useState(null);
  const [ctaData, setCtaData] = useState(null);

  const handleRichTextClick = (e) => {
    const target = e.target.closest('a');
    if (!target) return;
    const href = target.getAttribute('href');
    const linkTarget = target.getAttribute('target');
    if (!href || linkTarget === '_blank') return;

    if (href.startsWith('/') && !href.startsWith('//')) {
      e.preventDefault();
      navigate(href);
    } else if (href.startsWith('https://ahamhfc.com/') || href.startsWith('http://ahamhfc.com/')) {
      const path = href.replace(/^https?:\/\/ahamhfc\.com/, '');
      if (path.startsWith('/')) {
        e.preventDefault();
        navigate(path);
      }
    }
  };

  // Icon mapper
  const getIcon = (iconName, className) => {
    const icons = { Shield, Eye, UserCheck, Award, FileText, BarChart3, Target, Lock, AlertCircle, ShieldCheck, FileCheck, BookOpen, Users, TrendingUp, CheckCircle2 };
    const IconComponent = icons[iconName] || ShieldCheck;
    return <IconComponent className={className} />;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch Banner
        const bannerRes = await axios.get('/api/admin/banners/page/governance');
        if (bannerRes.data.success && bannerRes.data.data) {
          const banners = bannerRes.data.data;
          setBanner(Array.isArray(banners) ? banners[0] : banners);
        }

        // Fetch Foundation Section
        const foundationRes = await axios.get('/api/admin/governance/section/foundation');
        if (foundationRes.data.success && foundationRes.data.data) {
          setFoundation(foundationRes.data.data);
        }

        // Fetch Transparency Section
        const transparencyRes = await axios.get('/api/admin/governance/section/transparency');
        if (transparencyRes.data.success && transparencyRes.data.data) {
          setTransparency(transparencyRes.data.data);
        }

        // Fetch Oversight Section
        const oversightRes = await axios.get('/api/admin/governance/section/oversight');
        if (oversightRes.data.success && oversightRes.data.data) {
          setOversight(oversightRes.data.data);
        }

        // Fetch Ethics Section
        const ethicsRes = await axios.get('/api/admin/governance/section/ethics');
        if (ethicsRes.data.success && ethicsRes.data.data) {
          setEthics(ethicsRes.data.data);
        }

        // Fetch CTA Banner
        const ctaRes = await axios.get('/api/admin/cta-banner/settings/corporate_governance');
        if (ctaRes.data.success && ctaRes.data.data) {
          setCtaData(ctaRes.data.data);
        }

      } catch (error) {
        console.error('Error fetching governance data:', error);
      }
    };
    fetchData();
  }, []);

  // Parse foundation content
  const foundationPrinciples = foundation?.content_data
    ? (typeof foundation.content_data === 'string' ? JSON.parse(foundation.content_data) : foundation.content_data)
    : [];

  const displayPrinciples = (foundationPrinciples.length > 0 ? foundationPrinciples : [
    { icon: 'Shield', title: 'Integrity', description: 'In decision-making and business conduct', color: 'blue' }
  ]).filter(p => p.is_active !== false);

  // Parse transparency documents
  let governanceDocuments = [];
  if (transparency?.content_data) {
    const parsedDocs = typeof transparency.content_data === 'string'
      ? JSON.parse(transparency.content_data)
      : transparency.content_data;

    // Transform to match current structure if needed, primarily handling icon strings
    governanceDocuments = parsedDocs.filter(doc => doc.is_active !== false).map(doc => ({
      ...doc,
      icon: getIcon(doc.icon, "w-5 h-5") // Convert string icon name to component for display
    }));
  }

  // Fallback if no dynamic data
  if (governanceDocuments.length === 0) {
    governanceDocuments = [
      { name: 'Registration Certificate', icon: <FileText className="w-5 h-5" />, category: 'Certificates' },
      { name: 'Start adding documents in admin...', icon: <AlertCircle className="w-5 h-5" />, category: 'System' }
    ];
  }

  // Parse oversight committees
  let committees = [];
  if (oversight?.content_data) {
    const parsedCommittees = typeof oversight.content_data === 'string'
      ? JSON.parse(oversight.content_data)
      : oversight.content_data;

    committees = parsedCommittees.filter(c => c.is_active !== false).map(c => ({
      ...c,
      icon: getIcon(c.icon, "w-6 h-6")
    }));
  }

  // Fallback committees
  if (committees.length === 0) {
    committees = [
      { icon: <BarChart3 className="w-6 h-6" />, name: 'Audit Committee', description: 'Ensures financial integrity and internal control', color: 'blue' },
      { icon: <Users className="w-6 h-6" />, name: 'Remuneration Committee', description: 'Upholds fairness in leadership selection', color: 'green' }
    ];
  }

  // Parse ethics
  let complianceAreas = [];
  if (ethics?.content_data) {
    const parsedEthics = typeof ethics.content_data === 'string'
      ? JSON.parse(ethics.content_data)
      : ethics.content_data;

    complianceAreas = parsedEthics.filter(e => e.is_active !== false).map(e => ({
      ...e,
      icon: getIcon(e.icon, "w-5 h-5")
    }));
  }

  if (complianceAreas.length === 0) {
    complianceAreas = [
      { text: 'Adherence to guidelines', icon: <CheckCircle2 className="w-5 h-5" /> }
    ];
  }

  const improvementCommitments = [
    'Periodically review and update all internal policies',
    'Publish relevant information to keep stakeholders informed',
    'Conduct board evaluations and compliance assessments annually',
    'Encourage transparent feedback from customers, employees, and regulators'
  ];

  const filteredDocuments = governanceDocuments.filter(doc =>
    doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="corporate-governance">

      <SEO
        title="Corporate Governance & Policies | Aham Housing Finance"
        description="Explore Aham Housing Finance’s corporate governance framework. View our board of directors, ethical standards, and NHB compliance policies. Committed to integrity."
        keywords="Aham Housing Finance corporate governance, Aham corporate governance, corporate governance policy, housing finance corporate governance, internal guidelines on corporate governance, whistleblower policy, fit and proper policy, board of directors policy, audit committee policy, nomination and remuneration committee, risk management committee, asset liability management committee, compliance policy, governance framework, housing finance company policies, regulatory compliance housing finance, corporate policies Aham, board governance housing finance, transparency and disclosure policy, Aham governance documents"
        canonical="https://ahamhfc.com/corporate-governance-policies"
        ogTitle="Corporate Governance & Policies | Aham Housing Finance"
        ogDescription="Explore Aham Housing Finance’s corporate governance framework. View our board of directors, ethical standards, and NHB compliance policies. Committed to integrity."
      />

      {/* Hero Banner */}
      {/* Hero Banner */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('${banner?.image_url || "/img/Banners/Governance-Banner.webp"}')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-left">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl text-left"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-white mb-6 border border-white/20">
              <Shield className="w-5 h-5" />
              <span className="font-medium">GOVERNANCE</span>
            </div>
            <h1 className="text-3xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
              {banner?.title || "Corporate Governance"}
            </h1>
            <p className="text-lg md:text-3xl text-white/90 font-medium max-w-4xl leading-relaxed mb-8">
              {banner?.subtitle || "Integrity. Transparency. Accountability."}
            </p>

            {/* Breadcrumb Navigation */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="flex items-center space-x-2 mb-6 md:mb-8"
            >
              <Link
                to="/"
                className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
              >
                Home
              </Link>
              <ChevronRightIcon className="h-4 w-4 text-white/60" />
              <span className="text-yellow-300 text-sm font-semibold">Governance</span>
            </motion.div>

          </motion.div>
        </div>
      </section>

      {/* Section 1: Governance Philosophy (Foundation) */}
      <section className="philosophy-section">
        <div className="section-container">
          <div className="section-header-center text-center lg:text-left">
            <span className="section-tag mx-auto lg:mx-0">{foundation?.section_tag || 'Our Foundation'}</span>
            <h2 className="section-title mx-auto lg:mx-0">{foundation?.title || 'Built on Principles that Inspire Confidence'}</h2>
            <p className="section-description mx-auto lg:mx-0">
              {foundation?.description || 'Corporate governance at Aham HFC goes beyond compliance — it is a value system that drives responsible growth.'}
            </p>
          </div>

          <div className="principles-grid">
            {displayPrinciples.map((principle, index) => (
              <Card key={index} className={`principle-card ${principle.color}`}>
                <div className="principle-icon-wrapper">
                  {/* Handle both static component (if fallback) and dynamic string name */}
                  {typeof principle.icon === 'string'
                    ? getIcon(principle.icon, "w-7 h-7")
                    : principle.icon}
                </div>
                <h3 className="principle-title">{principle.title}</h3>
                <p className="principle-description">{principle.description}</p>
              </Card>
            ))}
          </div>

          <p className="philosophy-note">
            {foundation?.extra_text || 'We continuously strengthen these principles to deliver sustainable value and uphold public confidence.'}
          </p>
        </div>
      </section>

      {/* Section 2: Key Documents */}
      <section className="documents-section">
        <div className="section-container">
          <div className="documents-header text-center lg:text-left">
            <div>
              <span className="section-tag mx-auto lg:mx-0">{transparency?.section_tag || 'Transparency'}</span>
              <h2 className="section-title mx-auto lg:mx-0">{transparency?.title || 'Policies & Certificates'}</h2>
              <p className="documents-intro mx-auto lg:mx-0">
                {transparency?.description || 'Our governance structure is supported by clear, documented policies and disclosures.'}
              </p>
              {(transparency?.extra_text || DEFAULT_TRANSPARENCY_NOTICE) && (
                <div
                  onClick={handleRichTextClick}
                  className="documents-investor-notice mt-3 text-slate-600 text-sm md:text-base leading-relaxed [&_a]:text-primary-600 [&_a]:underline hover:[&_a]:text-primary-800 [&_a]:font-bold [&_a]:transition-colors cursor-pointer"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeAndFormatHtml(transparency?.extra_text || DEFAULT_TRANSPARENCY_NOTICE)
                  }}
                />
              )}
            </div>
            <div className="search-box">
              <Search className="search-icon" />
              <input
                type="text"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          <div className="documents-grid">
            {filteredDocuments.map((doc, index) => (
              <Card
                key={index}
                className="document-card cursor-pointer hover:shadow-lg transition-all"
                onClick={() => {
                  let fileUrl = doc.file_url || doc.link;
                  if (fileUrl) {
                    if (fileUrl.startsWith('http')) {
                      window.open(fileUrl, '_blank');
                    } else {
                      const envBackendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001/api';
                      const backendBase = envBackendUrl.replace(/\/api$/, '');

                      // Map legacy /pdfs/ links to proxied /api/pdfs/ links
                      if (fileUrl.startsWith('/pdfs/')) {
                        fileUrl = '/api' + fileUrl;
                      }

                      const separator = fileUrl.startsWith('/') ? '' : '/';
                      window.open(`${backendBase}${separator}${fileUrl}`, '_blank');
                    }
                  }
                }}
              >
                <div className="document-icon">{doc.icon}</div>
                <div className="document-content">
                  <h3 className="document-name">{doc.name}</h3>
                </div>
                <Button
                  variant="ghost"
                  className="document-download-btn p-0 h-auto"
                  onClick={(e) => {
                    e.stopPropagation();
                    let fileUrl = doc.file_url || doc.link;
                    if (fileUrl) {
                      if (fileUrl.startsWith('http')) {
                        window.open(fileUrl, '_blank');
                      } else {
                        const envBackendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001/api';
                        const backendBase = envBackendUrl.replace(/\/api$/, '');

                        if (fileUrl.startsWith('/pdfs/')) {
                          fileUrl = '/api' + fileUrl;
                        }

                        const separator = fileUrl.startsWith('/') ? '' : '/';
                        window.open(`${backendBase}${separator}${fileUrl}`, '_blank');
                      }
                    }
                  }}
                >
                  <Download className="w-4 h-4 text-primary-600" />
                </Button>
              </Card>
            ))}
          </div>

          {filteredDocuments.length === 0 && (
            <div className="no-results">
              <FileText className="no-results-icon" />
              <p>No documents found matching your search.</p>
            </div>
          )}

          {/* <p className="documents-note">
            Each document may be downloaded or viewed for full details.
          </p> */}
        </div>
      </section>

      {/* Section 3: Leadership & Committees */}
      <section className="leadership-section">
        <div className="section-container">
          <div className="section-header-center text-center lg:text-left">
            <span className="section-tag mx-auto lg:mx-0">{oversight?.section_tag || 'Oversight & Structure'}</span>
            <h2 className="section-title mx-auto lg:mx-0">{oversight?.title || 'Empowered Leadership, Accountable Governance'}</h2>
            <p className="section-description mx-auto lg:mx-0">
              {oversight?.description || 'Our governance model is anchored by a strong and independent Board of Directors.'}
            </p>
          </div>

          <div className="committees-grid">
            {committees.map((committee, index) => (
              <Card key={index} className={`committee-card ${committee.color}`}>
                <div className="committee-header">
                  <div className="committee-icon">{committee.icon}</div>
                </div>
                <h3 className="committee-name">{committee.name}</h3>
                <p className="committee-description">{committee.description}</p>
                {/* <Button variant="outline" className="committee-btn">
                  View Charter
                  <ExternalLink className="w-4 h-4" />
                </Button> */}
              </Card>
            ))}
          </div>

          {/* <Card className="charter-note">
            <Target className="charter-icon" />
            <p className="charter-text">
              Each committee functions under clearly defined charters, meeting regularly to uphold transparency and fairness across all operations.
            </p>
          </Card> */}
        </div>
      </section>

      {/* Section 4: Ethical Conduct */}
      <section className="ethics-section">
        <div className="section-container">
          <div className="ethics-layout">
            <div className="ethics-content text-center lg:text-left">
              <span className="section-tag mx-auto lg:mx-0">{ethics?.section_tag || 'Culture of Ethics'}</span>
              <h2 className="section-title-white mx-auto lg:mx-0">{ethics?.title || 'Doing the Right Thing — Always'}</h2>
              <p className="ethics-intro mx-auto lg:mx-0">
                {ethics?.description || 'We foster a culture of ethical behavior through continuous awareness and training.'}
              </p>
            </div>

            <div className="compliance-cards">
              {complianceAreas.map((area, index) => (
                <Card key={index} className={`compliance-card ${area.color || 'blue'}`}>
                  <div className="compliance-icon">{area.icon}</div>
                  <p className="compliance-text">{area.text}</p>
                </Card>
              ))}
            </div>
          </div>

          <p className="ethics-footer">
            {ethics?.extra_text || 'By promoting honesty and accountability, we ensure that every employee is an ambassador of trust.'}
          </p>
        </div>
      </section>

      {/* Section 5: Continuous Improvement */}
      {/* <section className="improvement-section">
        <div className="section-container">
          <div className="improvement-header">
            <RefreshCw className="refresh-icon" />
            <div>
              <span className="section-tag">Evolution & Growth</span>
              <h2 className="section-title">Commitment to Ongoing Governance Excellence</h2>
              <p className="improvement-intro">
                Governance is not static — it evolves with changing expectations and regulatory frameworks.
              </p>
            </div>
          </div>

          <div className="improvement-grid">
            {improvementCommitments.map((commitment, index) => (
              <Card key={index} className="improvement-card">
                <div className="improvement-number">{(index + 1).toString().padStart(2, '0')}</div>
                <p className="improvement-text">{commitment}</p>
                <CheckCircle2 className="improvement-check" />
              </Card>
            ))}
          </div>

          <Card className="governance-quote">
            <div className="quote-icon-wrapper">
              <Shield className="quote-icon" />
            </div>
            <blockquote className="quote-text">
              "True governance is achieved when integrity becomes a habit, not a requirement."
            </blockquote>
            <p className="quote-author">— Aham HFC</p>
          </Card>
        </div>
      </section> */}

      {/* CTA Section */}
      {/* CTA Section */}
      <section className="governance-cta">
        <div className="governance-cta-container">
          <Shield className="cta-governance-icon" />
          <h2 className="cta-governance-title">{ctaData?.title || "Access Our Governance Framework"}</h2>
          {ctaData?.description && (
            <p className="cta-governance-text">
              {ctaData.description}
            </p>
          )}
          <div className="cta-governance-actions">
            <Button
              variant="outline"
              className="cta-governance-secondary"
              onClick={() => navigate(ctaData?.button_link || "/contact")}
            >
              {ctaData?.button_text || "Contact Team"}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CorporateGovernance;